import { Component, OnInit, Input, Output, EventEmitter, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { AnalyticsService, SessionData } from '@shared/services/analytics.service';
import { CoursesAPIService } from '@shared/services/coursesApi.service';
import { AssessmentCriteria, AssessmentResult, AssessmentSettings, Course, ExperienceType } from '@shared/models/course.model';
import { LoadState } from '@app/main/analytics/loader-directive/loader.module';
import { AssessmentService } from '@shared/services/assessment.service';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { AssessmentEditDialogComponent } from '@app/main/apps/forms/assessment/assessment-edit-dialog/assessment-edit-dialog.component';
import { AssessmentReportDialogComponent } from './assessment-report-dialog/assessment-report-dialog.component';
import { AuthenticationService } from '@shared/services/authentication.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { GenericConfirmationDialogComponent } from '@shared/components/generic-confirmation-dialog/generic-confirmation-dialog.component';

const ASSESSMENTS_LIMIT = 20;
const MIN_AI_MSG_SENT = 2;
const EDITOR_FRAGMENT = 'editor';

@Component({
  selector: 'assessment',
  templateUrl: './assessment.component.html',
  styleUrls: ['./assessment.component.scss']
})

export class AssessmentComponent implements OnInit, OnChanges, OnDestroy {
  @Input() course: Course;
  @Input() date_from: Date;
  @Input() date_to: Date;
  @Input() programsMode: boolean = false;
  @Output() onExplore = new EventEmitter<{ sessionId: string, userName: string, course: Course }>();

  public search: string;
  public sessions: SessionData[] = [];
  public assessments: AssessmentResult[] = [];
  public assessmentSettings: AssessmentSettings | undefined;
  public criteriaArray: [number, AssessmentCriteria][];
  public loadStatus: string;
  private fragmentSubscription?: Subscription;

  constructor(
    private _assessmentService: AssessmentService,
    private _analyticsService: AnalyticsService,
    private _authenticationService: AuthenticationService,
    private _courseAPIService: CoursesAPIService,
    private _activatedRoute: ActivatedRoute,
    private _router: Router,
    private _dialog: MatDialog
  ) {
  }

  ngOnInit(): void {
    this.fragmentSubscription = this._activatedRoute.fragment.subscribe(fragment => {
      if (fragment === EDITOR_FRAGMENT) {
        this.openAssessmentEditDialog();
      }
    });

    this.updateAssessmentSettings();
    this.loadAssessments();
  }

  ngOnDestroy(): void {
    this.fragmentSubscription?.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const fromChanged = changes.date_from && !changes.date_from.isFirstChange() && changes.date_from.currentValue !== changes.date_from.previousValue;
    const toChanged = changes.date_to && !changes.date_to.isFirstChange() && changes.date_to.currentValue !== changes.date_to.previousValue;
    const searchChanged = changes.search && changes.search.isFirstChange();
    const courseChanged = changes.course && !changes.course.isFirstChange() && changes.course.currentValue.id !== changes.course.previousValue.id;
    if (fromChanged || toChanged || searchChanged || courseChanged) {
      this.updateAssessmentSettings();
      this.loadAssessments();
    }
  }

  editAssessment() {
    if (this.course.experience_type === ExperienceType.ai) {
      // Edit assessment in the simplified editor.
      const url = `${this.course.learnspace.hub.urlTitle}/${this.course.learnspace_id}/simulations/${this.course.id}`;
      this._router.navigate([url], { queryParams: { step: 5 } });
    } else {
      // Edit assessment in the legacy dialog.
      this._router.navigate([], {
        fragment: EDITOR_FRAGMENT,
        queryParamsHandling: 'preserve'
      });
    }
  }

  private openAssessmentEditDialog() {
    if (this.course.experience_type === ExperienceType.ai) {
      // Assessment should be edited in the simplified editor for these experiences.
      return;
    }

    const dialog = this._dialog.open(AssessmentEditDialogComponent, {
      width: 'min(64vw, 1200px)',
      height: 'min(90vh, 900px)',
      panelClass: 'learnspace-theme',
      data: { course: this.course },
      autoFocus: false
    });
    dialog.afterClosed().subscribe(updatedCourse => {
      this.updateAssessmentSettings();
      this._router.navigate([], {
        fragment: null,
        queryParamsHandling: 'preserve'
      });
    });
  }

  private updateAssessmentSettings() {
    this.assessmentSettings = this.course.information?.assessment;
    this.criteriaArray = this.assessmentSettings?.criterias.map((c, index) => [index, c]) || [];
  }

  searchUser(userName: string) {
    this.search = userName;
    this.loadAssessments();
  }

  loadAssessments() {
    this.loadStatus = LoadState.loading;
    this._assessmentService.getAssessments(this.course.id, ASSESSMENTS_LIMIT, this.search).subscribe(assessments => {
      this._analyticsService.getSessions(this.course.id, { userName: this.search }).subscribe(sessions => {
        this.updateData(assessments, sessions as SessionData[]);
        this.loadStatus = LoadState.completed;
      }, (e) => {
        this.loadStatus = LoadState.error;
      });
    }, (e) => {
      this.loadStatus = LoadState.error;
    });
  }

  private updateData(assessments: AssessmentResult[], sessions: SessionData[]) {
    // Associate the assessment with it's corresponding session
    assessments.forEach(assessment => {
      assessment.session = sessions.find(s => s._id === assessment.session_id);
    });
    // Remove assessments for which we could not find the associated session
    assessments = assessments.filter(a => a.session);
    // Sort assessments by session date (most recent first)
    assessments.sort((a, b) => compareDatesDescendingOrder(a.session.date, b.session.date));

    // Get medians
    const settingsCriterias = this.course.information?.assessment?.criterias || [];
    settingsCriterias.forEach(settingsCriteria => {
      const criterias = assessments
        .map(assessment => {
          return assessment.report.criterias.find(criteria => criteria.title === settingsCriteria.title);
        })
        .filter(c => !!c && !isNaN(c.grade));
      const grades = criterias.map(c => c.grade);
      const median = calculateMedian(grades);
      criterias.forEach(c => c.medianGrade = median);
    });

    this.assessments = assessments;
    this.sessions = sessions;
  }

  confirmAssessmentsRegeneration(): void {
    this._dialog
      .open(GenericConfirmationDialogComponent, {
        width: 'auto',
        height: 'auto',
        panelClass: ['learnspace-theme'],
        data: { 
          title: 'Are you sure you want to regenerate reports?',
          message: 'Regenerating reports will erase all existing ones and generate a new report for each session using the latest criteria.\nIf a learner has already generated a report and tries to view it again, they will see the new version instead.',
          proceedText: 'REGENERATE'
        }
      })
      .afterClosed()
      .pipe(filter(val => val === 'ok'))
      .subscribe(() => this.generateAssessments());
  }
  
  generateAssessments() {
    this.loadStatus = LoadState.loading;
    this.assessments = [];

    this._analyticsService.getSessions(this.course.id, { limit: ASSESSMENTS_LIMIT, minAiMsgSent: MIN_AI_MSG_SENT }).subscribe(sessions => {
      const sessionsList = sessions as SessionData[];

      if (sessionsList.length === 0) {
        this.loadStatus = LoadState.completed;
        return;
      }

      let responseCount = 0;
      const checkIfFinished = () => {
        responseCount++;
        if (responseCount >= sessionsList.length) {
          this.loadAssessments();
        }
      };

      sessionsList.map(async (session) => {
        this._assessmentService.generate(session._id, this.course.id, this._authenticationService.wondaUser.id, 'analytics-view').subscribe(assesssment => {
          checkIfFinished();
        }, (e) => {
          checkIfFinished();
        });
      });
    }, (e) => {
      this.loadStatus = LoadState.error;
    });
  }

  showAssessment(assessment: AssessmentResult) {
    const dialog = this._dialog.open(AssessmentReportDialogComponent, {
      width: 'min(80vw, 1200px)',
      height: 'min(85vh, 800px)',
      panelClass: 'learnspace-theme',
      data: { assessment, course: this.course },
      autoFocus: false
    });
    dialog.componentInstance.onExplore = this.onExplore;
  }

  exportAssessments() {
    this._assessmentService
      .getAssessmentsExportDataByCourseId(this.course.id);
  }

  openHelp(): void {
    window.open('https://help.spaces.wondavr.com/en/articles/9728097-ai-assessment');
  }

  toggleLearnerReport() {
    this.assessmentSettings.learnerReportEnabled = !this.assessmentSettings.learnerReportEnabled;
    const information = {
      ...this.course.information,
      assessment: { ...this.assessmentSettings }
    };

    this._courseAPIService.updateCourse(this.course.id, { information }).subscribe(c => {
      this.assessmentSettings = c.information?.assessment;
    });
  }

  hasSessions(): boolean {
    return this.sessions && this.sessions.length > 0;
  }

  hasAssessments(): boolean {
    if (this.search) {
      // We might have no assessments to display if we search with an non-existing user.
      // In that case, we still want to display the view as if there are assessments
      return true
    };

    return this.assessments && this.assessments.length > 0;
  }
}

const calculateMedian = (grades: number[]): number => {
  const sortedGrades = [...grades].sort((a, b) => a - b);
  const mid = Math.floor(sortedGrades.length / 2)
  
  let median: number;
  if (sortedGrades.length % 2 !== 0) {
    median = sortedGrades[mid];
  } else {
    median = (sortedGrades[mid - 1] + sortedGrades[mid]) / 2;
  }

  return median;
}

const compareDatesDescendingOrder = (a: Date | string, b: Date | string): number => {
  const aDate = a instanceof Date ? a : new Date(a);
  const bDate = b instanceof Date ? b : new Date(b);
  return bDate.getTime() - aDate.getTime();
}
